import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, MessageSquare, UserCheck, CheckCircle2 } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

const STEPS = [
  {
    n: "01",
    icon: BookOpen,
    title: "Learn first",
    text: "Explore our educational toolkits, interactive workbooks and planning resources to understand your opportunities, potential constraints and the questions worth asking.",
  },
  {
    n: "02",
    icon: MessageSquare,
    title: "Tell us about your project",
    text: "Share your location, development type and the planning assistance you're looking for.",
  },
  {
    n: "03",
    icon: UserCheck,
    title: "Find your planning professional",
    text: "Our planned matching service will help connect you with a suitably qualified consultant whose experience aligns with your project and location.",
  },
  {
    n: "04",
    icon: CheckCircle2,
    title: "Move forward with confidence",
    text: "Arrange a personalised, one-on-one consultation to discuss your individual planning needs and next steps.",
  },
];

const Book = () => (
  <div data-testid="planner-connect-page">
    <Seo
      title="Planner Connect — Coming Soon"
      description="Plan First Planner Connect is coming soon — a service connecting NSW property owners, homebuyers and developers with suitably qualified planning consultants."
      canonicalPath="/book"
    />

    <section className="bg-sand-light/60 py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <span
            className="inline-block bg-teal text-ivory text-[11px] font-mono uppercase tracking-[0.22em] px-4 py-1.5 rounded-full"
            data-testid="planner-connect-badge"
          >
            Coming Soon
          </span>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-teal leading-[1.08]">
            Your Planner. Your Project. Your Next Step.
          </h1>
          <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/55">
            From understanding your property to connecting with the right professional
          </p>
          <div className="mt-8 space-y-4 text-charcoal/75 leading-relaxed max-w-2xl mx-auto">
            <p>
              You've explored your opportunities, worked through your Plan First toolkit and
              started asking the right questions. Soon, we're taking the next step with you.
            </p>
            <p>
              Whether you're renovating, subdividing, building your dream home or exploring the
              potential of your land, our future Planner Connect service aims to help you find
              the right professional for your next step.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="bg-teal py-16 lg:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <Eyebrow dark>Introducing</Eyebrow>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ivory">
            Plan First Planner Connect
          </h2>
          <p className="mt-4 text-ivory/80 leading-relaxed max-w-2xl mx-auto">
            An exciting new service designed to connect NSW property owners, homebuyers and
            developers with suitably qualified planning consultants who understand their local
            planning requirements and the type of development they're pursuing.
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>How It Will Work</Eyebrow>
        </FadeIn>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cloud border border-cloud">
          {STEPS.map((s, i) => (
            <FadeIn key={s.n} delay={i * 0.07} className="h-full">
              <div className="h-full bg-white p-7" data-testid={`planner-step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="w-6 h-6 text-teal" />
                  <span className="font-mono text-xs text-sand">{s.n}</span>
                </div>
                <h3 className="mt-5 font-serif text-lg text-teal">{s.title}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{s.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-sand-light/60 border-t border-cloud">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-teal text-balance">
            A smarter way to approach planning is coming.
          </h2>
          <p className="mt-4 text-charcoal/70 leading-relaxed">
            Because finding the right answers starts with knowing the right questions.
          </p>
          <p className="mt-6 font-serif text-xl text-teal tracking-wide">PLAN FIRST. PLAN SMARTER.</p>
          <p className="mt-8 text-xs text-charcoal/55 leading-relaxed max-w-xl mx-auto" data-testid="planner-connect-notice">
            Planner Connect is currently in development. Professional consultations, consultant
            matching and bookings are not yet available. Any future consultation services will be
            provided independently by suitably qualified consultants under their own engagement
            terms — separate from Plan First NSW's educational resources.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/toolkits"
              data-testid="planner-connect-cta-toolkits"
              className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
            >
              Explore the toolkits <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="planner-connect-cta-contact"
              className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal hover:text-ivory transition-colors"
            >
              Register your interest
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default Book;
